/**
 * Statsig Event Utilities
 *
 * This file contains utility functions and helper classes for event tracking.
 */

import { envConfig } from "@configs/env";
import { StatsigUser } from "@statsig/client-core";
import { StatsigClient } from "@statsig/react-bindings";
import logger from "@utils/logger";
import { MemberModel } from "kz-ui-sdk";
import { EVENTS, EventName, REGISTRATION_FORM_FOCUS_SESSION_KEY, STATSIG_READY_EVENT } from "./constants";
import { DepositStep, RegistrationStep } from "./types";

// Shared in-flight promise to deduplicate concurrent readiness waits
let inflightWaitForReadyPromise: Promise<StatsigClient | null> | null = null;

// ===== UTILITY FUNCTIONS =====

export function isValidEvent(eventName: string): eventName is EventName {
  return Object.values(EVENTS).includes(eventName as EventName);
}

export function isUserAction(eventName: string): boolean {
  return eventName.startsWith("act:");
}

export function isSystemEvent(eventName: string): boolean {
  return eventName.startsWith("evt:");
}

// ===== DEPOSIT SUCCESS DEDUPLICATION =====
// Shared, capped, TTL-based cache to prevent duplicate deposit success exposure across modules
const MAX_DEPOSIT_TRACKED = 500;
const DEPOSIT_TRACK_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const seenDepositTransactions: Map<string, number> = new Map();

function pruneSeenDepositTransactions(): void {
  const now = Date.now();
  // Remove expired
  for (const [txId, ts] of seenDepositTransactions) {
    if (now - ts > DEPOSIT_TRACK_TTL_MS) {
      seenDepositTransactions.delete(txId);
    }
  }
  // Enforce cap by removing oldest
  const overflow = seenDepositTransactions.size - MAX_DEPOSIT_TRACKED;
  if (overflow > 0) {
    const iterator = seenDepositTransactions.keys();
    for (let i = 0; i < overflow; i++) {
      const oldestKey = iterator.next().value as string | undefined;
      if (oldestKey === undefined) break;
      seenDepositTransactions.delete(oldestKey);
    }
  }
}

export function hasSeenAndMarkDeposit(txId: string): boolean {
  pruneSeenDepositTransactions();
  if (seenDepositTransactions.has(txId)) return true;
  seenDepositTransactions.set(txId, Date.now());
  return false;
}

// ===== CLOSE DEPOSIT IFRAME DEDUPLICATION =====
const MAX_CLOSE_DEPOSIT_TRACKED = 1000;
const CLOSE_DEPOSIT_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const seenCloseDepositKeys: Map<string, number> = new Map();

function pruneSeenCloseDeposit(): void {
  const now = Date.now();
  for (const [key, ts] of seenCloseDepositKeys) {
    if (now - ts > CLOSE_DEPOSIT_TTL_MS) {
      seenCloseDepositKeys.delete(key);
    }
  }
  const overflow = seenCloseDepositKeys.size - MAX_CLOSE_DEPOSIT_TRACKED;
  if (overflow > 0) {
    const iterator = seenCloseDepositKeys.keys();
    for (let i = 0; i < overflow; i++) {
      const oldestKey = iterator.next().value as string | undefined;
      if (oldestKey === undefined) break;
      seenCloseDepositKeys.delete(oldestKey);
    }
  }
}

/**
 * Dedup by combination of orderRef + reason.
 * Returns true if the combo has been seen within TTL (event should be skipped).
 */
export function hasSeenAndMarkCloseDeposit(orderRef?: string | null, reason?: string | null): boolean {
  if (!orderRef || !reason) return false;
  pruneSeenCloseDeposit();
  const key = `${orderRef}:${reason}`;
  if (seenCloseDepositKeys.has(key)) return true;
  seenCloseDepositKeys.set(key, Date.now());
  return false;
}

/**
 * Helper class to track deposit funnel with proper step progression
 */
export class DepositFunnelTracker {
  private static instance: DepositFunnelTracker;
  private depositStartTime: number | null = null;
  private completedSteps: DepositStep[] = [];
  private attemptCounts: Record<string, number> = {};
  private selectedData: Record<string, any> = {};

  static getInstance(): DepositFunnelTracker {
    if (!DepositFunnelTracker.instance) {
      DepositFunnelTracker.instance = new DepositFunnelTracker();
    }
    return DepositFunnelTracker.instance;
  }

  startDeposit(): void {
    this.depositStartTime = Date.now();
    this.completedSteps = [];
    this.attemptCounts = {};
    this.selectedData = {};
  }

  addStep(step: DepositStep): void {
    if (!this.completedSteps.includes(step)) {
      this.completedSteps.push(step);
    }
  }

  incrementAttempt(eventType: string): number {
    this.attemptCounts[eventType] = (this.attemptCounts[eventType] || 0) + 1;
    return this.attemptCounts[eventType];
  }

  setSelectedData(key: string, value: any): void {
    this.selectedData[key] = value;
  }

  getSelectedData(key: string): any {
    return this.selectedData[key];
  }

  getTotalTime(): number {
    return this.depositStartTime ? Date.now() - this.depositStartTime : 0;
  }

  getCompletedSteps(): DepositStep[] {
    return [...this.completedSteps];
  }

  reset(): void {
    this.depositStartTime = null;
    this.completedSteps = [];
    this.attemptCounts = {};
    this.selectedData = {};
  }
}

// ===== REGISTRATION ANALYTICS HELPER =====

/**
 * Helper class to track registration funnel with proper step progression
 */
export class RegistrationFunnelTracker {
  private static instance: RegistrationFunnelTracker;
  private registrationStartTime: number | null = null;
  private completedSteps: RegistrationStep[] = [];
  private attemptCounts: Record<string, number> = {};

  static getInstance(): RegistrationFunnelTracker {
    if (!RegistrationFunnelTracker.instance) {
      RegistrationFunnelTracker.instance = new RegistrationFunnelTracker();
    }
    return RegistrationFunnelTracker.instance;
  }

  startRegistration(): void {
    this.registrationStartTime = Date.now();
    this.completedSteps = [];
    this.attemptCounts = {};
  }

  addStep(step: RegistrationStep): void {
    if (!this.completedSteps.includes(step)) {
      this.completedSteps.push(step);
    }
  }

  incrementAttempt(eventType: string): number {
    this.attemptCounts[eventType] = (this.attemptCounts[eventType] || 0) + 1;
    return this.attemptCounts[eventType];
  }

  getTotalTime(): number {
    return this.registrationStartTime ? Date.now() - this.registrationStartTime : 0;
  }

  getCompletedSteps(): RegistrationStep[] {
    return [...this.completedSteps];
  }

  reset(): void {
    this.registrationStartTime = null;
    this.completedSteps = [];
    this.attemptCounts = {};
  }
}

// ===== BRAND GROUP MAPPING =====

const BRAND_GROUP_MAP: Record<string, string> = {
  kz: "kzg1",
  kz2: "kzg2",
  bl1: "blg1",
  wdb1: "wdb1",
  bk: "96g1",
  phk1: "phk1",
  idk1: "idk1",
  bdk1: "bdk1",
  pkk1: "pkk1",
};

export function mapBrandGroup(input: string | undefined | null): string | undefined {
  if (!input) return undefined;
  return BRAND_GROUP_MAP[input] ?? input;
}

export function getGlobalStatsigClient(): StatsigClient | null {
  return ((window as any).__statsig_client__ as StatsigClient | null | undefined) || null;
}

export async function waitForStatsigReadyEvent(timeoutMs: number = 10000): Promise<StatsigClient | null> {
  logger._console.log("[Statsig] waitForStatsigReadyEvent: start", { timeoutMs });
  const existingClient = getGlobalStatsigClient();
  if (existingClient && (existingClient as any).loadingStatus === "Ready") {
    logger._console.log("[Statsig] waitForStatsigReadyEvent: client already ready; returning immediately");
    return existingClient;
  }

  // If a wait is already in-flight, reuse it to avoid duplicate listeners
  if (inflightWaitForReadyPromise) {
    logger._console.log("[Statsig] waitForStatsigReadyEvent: reusing in-flight promise");
    return inflightWaitForReadyPromise;
  }

  inflightWaitForReadyPromise = new Promise<StatsigClient | null>((resolve) => {
    let resolved = false;

    // Schedule timeout first to safely reference inside onReady without TDZ issues
    const timeoutId = window.setTimeout(() => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener(STATSIG_READY_EVENT, onReady as EventListener);
      logger._console.warn("[Statsig] waitForStatsigReadyEvent: timeout reached; returning current global client", {
        timeoutMs,
      });
      resolve(getGlobalStatsigClient());
    }, timeoutMs);

    logger._console.log("[Statsig] waitForStatsigReadyEvent: listening for", STATSIG_READY_EVENT);
    const onReady = (event: Event) => {
      if (resolved) return;
      resolved = true;
      logger._console.log("[Statsig] waitForStatsigReadyEvent: received ready event");
      const detailClient = (event as CustomEvent).detail?.client as StatsigClient | undefined;
      window.removeEventListener(STATSIG_READY_EVENT, onReady as EventListener);
      clearTimeout(timeoutId);
      resolve(detailClient ?? getGlobalStatsigClient());
    };

    window.addEventListener(STATSIG_READY_EVENT, onReady as EventListener, { once: true });
  });

  // Clear the shared handle after the current attempt settles
  inflightWaitForReadyPromise.finally(() => {
    inflightWaitForReadyPromise = null;
  });

  return inflightWaitForReadyPromise;
}

export function buildUserProperties(member: MemberModel, serverPrefix: string): StatsigUser {
  return {
    userID: `${serverPrefix || ""}${member.apiIdentifier || ""}` || member.id,
    custom: {
      memberId: member.id,
      accountId: member.accountId || envConfig?.accountId,
      group: mapBrandGroup(envConfig?.brand?.group),
      brand: envConfig?.brand?.key,
      ...(member.primaryWallet && {
        walletBalance: member.primaryWallet.balance?.toString(),
        walletId: member.primaryWallet.id,
      }),
      memberGroupId: member?.group?.groupId || undefined,
      memberGroupName: member?.group?.group?.name || undefined,
      appVersion: import.meta.env.VITE_APP_VERSION,
    },
    customIDs: {
      accountId: member.accountId || envConfig?.accountId,
      group: mapBrandGroup(envConfig?.brand?.group),
      brand: envConfig?.brand?.key,
    },
  };
}

// ===== SESSION FLAG UTILITIES =====

export function hasSeenAndMarkOncePerSession(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, "1");
    return false;
  } catch {
    return false;
  }
}

export function clearOncePerSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

export function hasSeenAndMarkRegistrationFormFocus(): boolean {
  return hasSeenAndMarkOncePerSession(REGISTRATION_FORM_FOCUS_SESSION_KEY);
}

export function resetRegistrationFormFocusOncePerSession(): void {
  clearOncePerSession(REGISTRATION_FORM_FOCUS_SESSION_KEY);
}
